import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ToastMsg from "../../components/ToastMsg/ToastMsg";
import config from "../../config/config";
import "./SpecialPayments.scss";

const ADMIN_TOKEN_KEY = "admin_jwt_archinza";

const SpecialPayments = () => {
  const [adminToken, setAdminToken] = useState(
    () => localStorage.getItem(ADMIN_TOKEN_KEY) || ""
  );

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Form state
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState("");
  const [amount, setAmount] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  // Links dashboard
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${adminToken}` } }),
    [adminToken]
  );

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await axios.get(
        `${config.api_url}/admin/special-payments/businesses`,
        authHeaders()
      );
      if (res.data?.data) setBusinesses(res.data.data);
    } catch {
      toast(
        <ToastMsg message="Failed to load businesses" danger />,
        config.error_toast_config
      );
    }
  }, [authHeaders]);

  const fetchLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const res = await axios.get(
        `${config.api_url}/admin/special-payments/links`,
        authHeaders()
      );
      if (res.data?.data) setLinks(res.data.data);
    } catch {
      toast(
        <ToastMsg message="Failed to load links" danger />,
        config.error_toast_config
      );
    } finally {
      setLinksLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (adminToken) {
      fetchBusinesses();
      fetchLinks();
    }
  }, [adminToken, fetchBusinesses, fetchLinks]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${config.api_url}/admin/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      const token = res.data?.data?.token;
      if (token) {
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        setAdminToken(token);
      } else {
        toast(
          <ToastMsg message="Invalid credentials" danger />,
          config.error_toast_config
        );
      }
    } catch {
      toast(
        <ToastMsg message="Login failed" danger />,
        config.error_toast_config
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminToken("");
    setBusinesses([]);
    setLinks([]);
    setGeneratedLink("");
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !amount || Number(amount) <= 0) {
      toast(
        <ToastMsg message="Select a business and enter a valid amount" danger />,
        config.error_toast_config
      );
      return;
    }
    setGenerating(true);
    setGeneratedLink("");
    try {
      const res = await axios.post(
        `${config.api_url}/admin/special-payments/generate`,
        { businessId: selectedBusiness, amount: Number(amount) },
        authHeaders()
      );
      const token = res.data?.data?.token;
      if (token) {
        const link = `${window.location.origin}/special-checkout/${token}`;
        setGeneratedLink(link);
        fetchLinks();
      }
    } catch {
      toast(
        <ToastMsg message="Failed to generate link" danger />,
        config.error_toast_config
      );
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      toast(<ToastMsg message="Link copied!" />, config.success_toast_config);
    });
  };

  const statusBadge = (status) => {
    const styles = {
      used: { background: "#d1fae5", color: "#065f46" },
      expired: { background: "#fee2e2", color: "#991b1b" },
      unused: { background: "#dbeafe", color: "#1e40af" },
    };
    return (
      <span className="sp__badge" style={styles[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (!adminToken) {
    return (
      <div className="sp__login-wrap">
        <div className="sp__login-card">
          <h2 className="sp__login-title">Admin Login</h2>
          <p className="sp__login-sub">Special Payments — Team Access</p>
          <form onSubmit={handleLogin} className="sp__login-form">
            <label className="sp__label">Email</label>
            <input
              type="email"
              className="sp__input"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              autoFocus
            />
            <label className="sp__label">Password</label>
            <input
              type="password"
              className="sp__input"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
            <button
              type="submit"
              className="sp__btn sp__btn--primary"
              disabled={loginLoading}
            >
              {loginLoading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="sp__wrap">
      <div className="sp__header">
        <h1 className="sp__title">Special Payments</h1>
        <button className="sp__btn sp__btn--ghost" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Generate link form */}
      <div className="sp__card">
        <h2 className="sp__card-title">Generate Discount Link</h2>
        <form onSubmit={handleGenerate} className="sp__form">
          <div className="sp__form-row">
            <div className="sp__form-group">
              <label className="sp__label">Starter Plan Business</label>
              <select
                className="sp__input"
                value={selectedBusiness}
                onChange={(e) => setSelectedBusiness(e.target.value)}
                required
              >
                <option value="">— Select a business —</option>
                {businesses.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.business_name} ({b.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="sp__form-group">
              <label className="sp__label">Special Amount (₹)</label>
              <input
                type="number"
                className="sp__input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 999"
                min="1"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className="sp__btn sp__btn--primary"
            disabled={generating}
          >
            {generating ? "Generating..." : "Generate Link"}
          </button>
        </form>

        {generatedLink && (
          <div className="sp__generated">
            <p className="sp__generated-label">Shareable Link</p>
            <div className="sp__generated-row">
              <span className="sp__generated-url">{generatedLink}</span>
              <button
                className="sp__btn sp__btn--copy"
                onClick={() => copyLink(generatedLink)}
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Links dashboard */}
      <div className="sp__card">
        <div className="sp__card-header-row">
          <h2 className="sp__card-title">Generated Links</h2>
          <button
            className="sp__btn sp__btn--ghost"
            onClick={fetchLinks}
            disabled={linksLoading}
          >
            {linksLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {links.length === 0 && !linksLoading && (
          <p className="sp__empty">No links generated yet.</p>
        )}

        {links.length > 0 && (
          <div className="sp__table-wrap">
            <table className="sp__table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Email</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Used At</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => {
                  const linkUrl = `${window.location.origin}/special-checkout/${l.token}`;
                  return (
                    <tr key={l._id}>
                      <td>{l.businessId?.business_name || "—"}</td>
                      <td>{l.businessId?.email || "—"}</td>
                      <td>₹{l.amount}</td>
                      <td>{statusBadge(l.status)}</td>
                      <td>
                        {new Date(l.expiresAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td>
                        {l.usedAt
                          ? new Date(l.usedAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td>
                        {l.status !== "used" && (
                          <button
                            className="sp__btn sp__btn--copy"
                            onClick={() => copyLink(linkUrl)}
                          >
                            Copy
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialPayments;
